/* ────────────────────────────────────────────────
   src/components/quote/StepBillReview.jsx
───────────────────────────────────────────────── */
import React from "react";
import { Box, Typography, Button, Stack, alpha, useTheme } from "@mui/material";

export default function StepBillReview({ defaultData, onComplete, onBack }) {
  if (!defaultData) return null;
  const theme = useTheme();

  const {
    promedioDiario,
    kwPotenciaReq,
    numSP585,
    kwPotenciaFinal,
    precioSistemaUsd,
    precioSistemaMxn,
    retornoAnios,
    creditoMes12,
  } = defaultData;

  const fmt = (n, d = 2) =>
    typeof n === "number"
      ? n.toLocaleString("es-MX", { minimumFractionDigits: d })
      : "—";

  const SECTIONS = [
    ["Consumo", [["Promedio diario", `${fmt(promedioDiario)} kWh`]]],
    [
      "Sistema propuesto",
      [
        ["Potencia requerida", `${fmt(kwPotenciaReq)} kW`],
        ["Paneles (585 W)", numSP585],
        ["Potencia final", `${fmt(kwPotenciaFinal)} kW`],
      ],
    ],
    [
      "Finanzas",
      [
        ["Precio sistema USD", `$${fmt(precioSistemaUsd)}`],
        ["Precio sistema MXN", `$${fmt(precioSistemaMxn)}`],
        ["Retorno inversión", `${fmt(retornoAnios, 1)} años`],
        ["Crédito 12 m sin interés", `$${fmt(creditoMes12)}`],
      ],
    ],
  ];

  const Row = ({ label, value, last }) => (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "baseline",
        py: 1.25,
        borderBottom: last
          ? "none"
          : `1px solid ${alpha(theme.palette.divider, 0.15)}`,
      }}
    >
      <Typography
        variant="subtitle2"
        sx={{
          fontFamily: '"Ethos Nova", sans-serif',
          color: theme.palette.text.secondary,
          fontWeight: 600,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="subtitle1"
        sx={{
          fontFamily: '"Ethos Nova", sans-serif',
          fontWeight: 900,
        }}
      >
        {value}
      </Typography>
    </Box>
  );

  return (
    <Box
      component="section"
      sx={{
        maxWidth: 860,
        mx: "auto",
        px: { xs: 2, md: 4 },
        py: { xs: 5, md: 7 },
      }}
    >
      <Typography
        variant="h5"
        fontWeight={900}
        mb={4}
        sx={{ fontFamily: '"Ethos Nova", sans-serif' }}
      >
        Resumen de tu cotización
      </Typography>

      {SECTIONS.map(([title, items]) => (
        <Box
          key={title}
          sx={{
            mb: { xs: 4, md: 5.5 },
            borderRadius: 4,
            bgcolor: alpha(theme.palette.background.paper, 0.9),
            boxShadow: `0 6px 18px ${alpha("#000", 0.06)}`,
            overflow: "hidden",
          }}
        >
          {/* cabecera destacada */}
          <Box
            sx={{
              px: 3,
              py: 2.5,
              bgcolor: alpha(theme.palette.primary.main, 0.14),
            }}
          >
            <Typography
              variant="subtitle1"
              fontWeight={900}
              sx={{
                fontFamily: '"Ethos Nova", sans-serif',
                color: theme.palette.primary.dark,
              }}
            >
              {title}
            </Typography>
          </Box>

          {/* filas */}
          <Box sx={{ px: 3 }}>
            {items.map(([label, value], idx) => (
              <Row
                key={label}
                label={label}
                value={value}
                last={idx === items.length - 1}
              />
            ))}
          </Box>
        </Box>
      ))}

      {/* botones */}
      <Stack
        direction={{ xs: "column-reverse", sm: "row" }}
        spacing={2}
        justifyContent="flex-end"
        mt={5}
      >
        <Button onClick={onBack} sx={{ textTransform: "none" }}>
          Atrás
        </Button>
        <Button
          variant="contained"
          onClick={() => onComplete(defaultData)}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            px: 4,
            py: 1.5,
          }}
        >
          Confirmar
        </Button>
      </Stack>
    </Box>
  );
}
